import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";

/**
 * sha256 hex digest of a UTF-8 string or buffer.
 *
 * Pure-JS via `@noble/hashes` so this module runs identically in Node, the browser
 * (Vite/PWA), and Workers — no `node:crypto` dependency. Output is a 64-char
 * lowercase hex string.
 */
export function sha256Hex(input: string | Uint8Array): string {
  const bytes = typeof input === "string" ? new TextEncoder().encode(input) : input;
  return bytesToHex(sha256(bytes));
}

/**
 * Canonical sha256 of a JSON value: object keys are sorted recursively so that the
 * same logical content always produces the same digest. Used by every artifact whose
 * digest needs to be deterministic across runs.
 */
export function canonicalSha256(value: unknown): string {
  return sha256Hex(canonicalJson(value));
}

export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

/**
 * Strict JSON-only canonicalization. We deliberately reject types that JSON.stringify
 * mishandles (BigInt throws; Date / Map / Set become "{}"; NaN/Infinity become null;
 * Symbol/Function get dropped silently). Callers that have such values must convert
 * them at the schema boundary before hashing — that keeps every artifact's digest
 * stable and inspectable. Fixes F2 from the Phase-1 review.
 */
function canonicalize(value: unknown): unknown {
  if (value === null) return null;
  const t = typeof value;
  if (t === "string" || t === "boolean") return value;
  if (t === "number") {
    if (!Number.isFinite(value as number)) {
      throw new TypeError(`canonicalSha256: non-finite number ${String(value)} is not JSON-encodable`);
    }
    return value;
  }
  if (t === "bigint") {
    throw new TypeError("canonicalSha256: BigInt is not JSON-encodable; convert to string first");
  }
  if (t === "undefined" || t === "function" || t === "symbol") {
    throw new TypeError(`canonicalSha256: ${t} is not JSON-encodable`);
  }
  if (value instanceof Date || value instanceof Map || value instanceof Set) {
    throw new TypeError(
      `canonicalSha256: ${value.constructor.name} is not JSON-encodable; convert at the schema boundary`
    );
  }
  if (Array.isArray(value)) return value.map(canonicalize);
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined) continue; // mirror JSON.stringify behavior: undefined keys are dropped
    out[k] = canonicalize(v);
  }
  return out;
}
